import { useEffect, useState } from "react";
import { Plus, Trash2, CalendarX, Flag, Check, X } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

export default function Exceptions() {
  const [exceptions, setExceptions] = useState(null);
  const [holidays, setHolidays] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [form, setForm] = useState({ date: "", type: "closed", employee_id: "", ranges: [] });
  const [holidayForm, setHolidayForm] = useState({ date: "", name: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    API.get("/exceptions")
      .then((r) => setExceptions(r.data))
      .catch((e) => toast.error(formatApiError(e)));

    API.get("/holidays")
      .then((r) => {
        const data = r.data;
        if (data && Array.isArray(data.holidays)) {
          setHolidays(data.holidays);
          if (data.newly_generated) {
            toast.info(
              `Les jours fériés de ${data.year} ont été mis à jour automatiquement. ` +
              `N'hésitez pas à faire un tour sur la rubrique exceptions pour supprimer ou modifier ceux que votre société souhaite travailler.`,
              { duration: 9000 }
            );
          }
        } else if (Array.isArray(data)) {
          setHolidays(data);
        } else {
          setHolidays([]);
        }
      })
      .catch(() => {});

    API.get("/employees")
      .then((r) => setEmployees(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";

  const saveException = async () => {
    setSaving(true);
    try {
      await API.post("/exceptions", {
        ...form,
        employee_id: form.employee_id || null,
        ranges: form.type === "open" ? form.ranges : [],
      });
      toast.success("Exception ajoutée");
      setOpen(false);
      setForm({ date: "", type: "closed", employee_id: "", ranges: [] });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveHoliday = async () => {
    setSaving(true);
    try {
      await API.post("/holidays", { ...holidayForm, auto_close: true });
      toast.success("Jour férié ajouté");
      setHolidayOpen(false);
      setHolidayForm({ date: "", name: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleHolidayAutoClose = async (h) => {
    const updatedStatus = !h.auto_close;
    try {
      await API.patch(`/holidays/${h.id}`, { auto_close: updatedStatus });
      setHolidays((prev) =>
        prev.map((item) => (item.id === h.id ? { ...item, auto_close: updatedStatus } : item))
      );
      toast.success(
        updatedStatus
          ? `${h.name} est désormais marqué comme fermé (chômé).`
          : `${h.name} est désormais marqué comme travaillé.`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteHoliday = async (id) => {
    try {
      await API.delete(`/holidays/${id}`);
      setHolidays((prev) => prev.filter((h) => h.id !== id));
      toast.success("Jour férié supprimé du calendrier");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-8" data-testid="exceptions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Exceptions</h1>
          <p className="mt-1 text-sm text-slate-500">
            Fermetures exceptionnelles, ouvertures spéciales, congés. Prioritaires sur les horaires habituels.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="btn-new-exception">
          <Plus className="mr-2 h-4 w-4" /> Nouvelle exception
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="exceptions-list">
        {exceptions === null ? (
          <p className="p-6 text-sm text-slate-400">Chargement...</p>
        ) : exceptions.length === 0 ? (
          <p className="p-6 text-sm text-slate-400" data-testid="exceptions-empty">
            Aucune exception.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {exceptions.map((ex) => (
              <li key={ex.id} className="flex items-center gap-3 px-5 py-3" data-testid={`exception-row-${ex.id}`}>
                <CalendarX className={`h-4 w-4 shrink-0 ${ex.type === "closed" ? "text-red-500" : "text-emerald-500"}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium capitalize text-slate-900">
                    {new Date(ex.date + "T12:00:00").toLocaleDateString("fr-FR", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                  <p className="text-xs text-slate-500">
                    {ex.type === "closed"
                      ? "Fermé"
                      : `Ouvert : ${ex.ranges.map((r) => `${r.start} →${r.end}`).join(" · ")}`}
                    {ex.employee_id ? ` · ${empName(ex.employee_id)}` : " · Toute l'entreprise"}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600"
                  onClick={async () => {
                    await API.delete(`/exceptions/${ex.id}`);
                    load();
                  }}
                  data-testid={`btn-delete-exception-${ex.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Jours fériés</h2>
          <p className="mt-1 text-sm text-slate-500">
            Générés automatiquement chaque année. Vous pouvez désactiver la fermeture automatique ou supprimer un jour férié si vous travaillez (ex: Pentecôte).
          </p>
        </div>
        <Button variant="outline" onClick={() => setHolidayOpen(true)} data-testid="btn-new-holiday">
          <Flag className="mr-2 h-4 w-4" /> Ajouter un jour férié
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="holidays-list">
        {holidays === null ? (
          <p className="p-6 text-sm text-slate-400">Chargement...</p>
        ) : holidays.length === 0 ? (
          <p className="p-6 text-sm text-slate-400" data-testid="holidays-empty">
            Aucun jour férié configuré.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {holidays.map((h) => (
              <li key={h.id} className="flex items-center gap-3 px-5 py-3" data-testid={`holiday-row-${h.id}`}>
                <Flag className={`h-4 w-4 shrink-0 ${h.auto_close ? "text-amber-500" : "text-slate-300"}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900">{h.name}</p>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        h.auto_close ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {h.auto_close ? "Fermé (auto)" : "Travaillé (ouvert)"}
                    </span>
                  </div>
                  <p className="text-xs capitalize text-slate-500">
                    {new Date(h.date + "T12:00:00").toLocaleDateString("fr-FR", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => toggleHolidayAutoClose(h)}
                  data-testid={`btn-toggle-holiday-${h.id}`}
                >
                  {h.auto_close ? (
                    <>
                      <Check className="mr-1 h-3 w-3 text-emerald-600" /> Ouvrir
                    </>
                  ) : (
                    <>
                      <X className="mr-1 h-3 w-3 text-amber-600" /> Fermer
                    </>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600"
                  onClick={() => deleteHoliday(h.id)}
                  data-testid={`btn-delete-holiday-${h.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="exception-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Nouvelle exception</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="exception-date"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="exception-type" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="closed">Fermé (congé, vacances, événement)</SelectItem>
                  <SelectItem value="open">Ouvert avec horaires spéciaux</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Concerné</Label>
              <Select
                value={form.employee_id || "all"}
                onValueChange={(v) => setForm({ ...form, employee_id: v === "all" ? "" : v })}
              >
                <SelectTrigger data-testid="exception-employee" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toute l'entreprise</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.type === "open" && (
              <div className="space-y-2">
                <Label>Horaires d'ouverture</Label>
                {(form.ranges.length ? form.ranges : []).map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="time"
                      value={r.start}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          ranges: form.ranges.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)),
                        })
                      }
                      className="h-10 rounded-md border border-slate-300 px-2 text-sm"
                      data-testid={`exception-start-${i}`}
                    />
                    <span className="text-slate-400">→</span>
                    <input
                      type="time"
                      value={r.end}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          ranges: form.ranges.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)),
                        })
                      }
                      className="h-10 rounded-md border border-slate-300 px-2 text-sm"
                      data-testid={`exception-end-${i}`}
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setForm({ ...form, ranges: [...form.ranges, { start: "09:00", end: "13:00" }] })}
                  data-testid="exception-add-range"
                >
                  <Plus className="mr-1 h-3 w-3" /> Ajouter une plage
                </Button>
              </div>
            )}
            <Button
              className="w-full"
              onClick={saveException}
              disabled={!form.date || saving || (form.type === "open" && form.ranges.length === 0)}
              data-testid="exception-submit"
            >
              {saving ? "Enregistrement..." : "Ajouter l'exception"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={holidayOpen} onOpenChange={setHolidayOpen}>
        <DialogContent data-testid="holiday-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Ajouter un jour férié</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
                data-testid="holiday-date"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Nom</Label>
              <Input
                value={holidayForm.name}
                onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                placeholder="Noël"
                data-testid="holiday-name"
                className="mt-1"
              />
            </div>
            <Button
              className="w-full"
              onClick={saveHoliday}
              disabled={!holidayForm.date || !holidayForm.name || saving}
              data-testid="holiday-submit"
            >
              {saving ? "Enregistrement..." : "Ajouter"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}